import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule, NgFor, NgIf, DecimalPipe, CurrencyPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule, MatTooltip } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';

import { CondicaoQualidadeStateService, EtapasDeEdicaoService, MemoriaDeCalculoService, LocalizacaoService, NovasSalasEdicaoCsvService, AcessoOfertaEducacionalStateService } from '../../services';
import { PrecoEncargosAdicionaisStateService } from '../../services/preco-encargos-adicionais-state/preco-encargos-adicionais-state.service';
import { PQRNovasSalasPrediosDto, PqrQuantidadeNovasConstrucoesDto, MemoriaDeCalculoDto } from '../../dtos';
import { ValidacaoResult, ValidadorTela } from '../../interfaces/validacao.interface';
import { TabelaComponent } from '../../shared/tabela/tabela.component';
import { MenuSuspensoComponent } from '../../shared/menu-suspenso/menu-suspenso.component';
import { MaskitoDirective } from '@maskito/angular';
import { DialogRestaurarValoresComponent } from '../../shared/dialog-restaurar-valores/dialog-restaurar-valores.component';
import { maskitoNumberOptionsGenerator } from '@maskito/kit';
import { RodapeFonteNotaComponent } from '../../shared/rodape-fonte-nota/rodape-fonte-nota.component';
import { FonteNotaInterface } from '../../shared/rodape-fonte-nota/fonte-nota.interface';
import { TipoSimulacao } from '../../enums/simulation-type.enum';
import { MatButton } from '@angular/material/button';
import { AnosReferenciasService } from '../../services/anos-referencias/anos-referencias.service';
import { AnosReferenciasDto } from '../../dtos/anos-referencias.dto';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';

@Component({
  selector: 'app-novas-salas-predios',
  standalone: true,
  imports: [
    CommonModule,
    NgFor,
    NgIf,
    FormsModule,
    MatIconModule,
    MatTooltipModule,
    TabelaComponent,
    MaskitoDirective,
    DecimalPipe,
    CurrencyPipe,
    RodapeFonteNotaComponent,
    MenuSuspensoComponent,
    MatButton,
  ],
  templateUrl: './novas-salas-predios.component.html',
  styleUrls: ['./novas-salas-predios.component.scss'],
})
export class NovasSalasPrediosComponent implements OnInit, ValidadorTela {
  @ViewChild('infoTooltip') infoTooltip!: MatTooltip;
  @ViewChild('menuTooltip') menuTooltip!: MatTooltip;

  tooltipVisivel = false;
  pqrsDisponiveis: PQRNovasSalasPrediosDto[] = [];
  overrides: PqrQuantidadeNovasConstrucoesDto[] = [];
  memoriaDeCalculo: MemoriaDeCalculoDto | null = null;
  edicaoBloqueada = false;
  isDownloadCsvNovasSalasEdicaoCarregando = false;

  readonly maskInteiro = maskitoNumberOptionsGenerator({
    precision: 0,
    min: 0,
    thousandSeparator: '.',
  });

  notasTabelaDemanda: FonteNotaInterface[] = [
    {
      titulo: 'Notas',
      conteudos: [
        {
          numero: 1,
          descricao:
            'Número de salas existentes calculado a partir dos dados disponibilizados pela Secretaria Municipal de Educação - dados atualizados.',
        },
        {
          numero: 2,
          descricao:
            'A estimativa do número de salas necessárias é feita com base no número de turmas por turno (parcial e integral) e a etapa de atendimento (separando EI das demais etapas). Considerando que a jornada parcial é de 4h a 5h, a cada duas turmas parciais diurnas se estima uma sala; no caso do integral, a cada turma integral se estima uma sala. O noturno é contabilizado no diurno parcial, mas ele apenas é considerado para o cálculo de salas necessárias quando o seu valor é maior que a soma de salas do diurno e integral.',
        },
        {
          numero: 3,
          descricao:
            'A demanda é calculada com base na diferença entre o número de salas existentes levantado pelo Censo Escolar e o número de salas necessárias calculado pelo simulador em cada escola; portanto, a demanda não se refere à diferença entre o existente e o necessário.',
        },
      ],
    },
  ];

  notasTabelaConstrucoes: FonteNotaInterface[] = [
    {
      titulo: 'Notas',
      conteudos: [
        {
          numero: 4,
          descricao:
            'Utiliza como padrão os tipos de construção previstos para o FNDE no âmbito do Plano de Ações Articuladas - Programa Proinfância e Infraestrutura Escolar. No caso das escolas de 2, 5, 9 e 13 salas elas são modelos pensadas para o EF, mas são utilizadas aqui como referência para o EM e EJA.',
        },
        {
          numero: 5,
          descricao:
            'Valores estimados com base nas Planilhas Orçamentárias do FNDE para as construções escolares de cada estado, valores atualizados em 2025. Os valores não incluem o terreno e desenvolvimento do projeto básico (projeto de fundações e elaboração da planilha de licitação)',
        },
      ],
    },
  ];

  constructor(
    private toastr: ToastrService,
    private dialogRestaurarValores: MatDialog,
    private etapasDeEdicaoService: EtapasDeEdicaoService,
    private condicaoQualidadeStateService: CondicaoQualidadeStateService,
    private memoriaDeCalculoService: MemoriaDeCalculoService,
    private localizacaoService: LocalizacaoService,
    private novasSalasEdicaoCsvService: NovasSalasEdicaoCsvService,
    private acessoOfertaEducacionalStateService: AcessoOfertaEducacionalStateService,
    private precoEncargosAdicionaisStateService: PrecoEncargosAdicionaisStateService,
    private anosReferenciasService: AnosReferenciasService,
    private featureFlagService: FeatureFlagService
  ) {
    let anosReferencias: AnosReferenciasDto | null = null;
    if (this.featureFlagService.funcionalidadeHabilitada('940-atualizacao-de-componentes-de-edicao-pqr')) {
      anosReferencias = this.anosReferenciasService.obterAnosReferenciasSnapshot();
    }
    const anoCenso = anosReferencias?.anoCenso ?? 2025;
    this.notasTabelaConstrucoes[0].conteudos[1].descricao = `Valores estimados com base nas Planilhas Orçamentárias do FNDE para as construções escolares de cada estado, valores atualizados em ${anoCenso}. Os valores não incluem o terreno e desenvolvimento do projeto básico (projeto de fundações e elaboração da planilha de licitação)`;
  }

  ngOnInit(): void {
    this.etapasDeEdicaoService.setOpcaoAtual(13);
    this.verificarBloqueioEdicao();
    this.carregarPqrs();
    this.carregarOverrides();
    this.carregarMemoriaDeCalculo();
  }

  verificarBloqueioEdicao(): void {
    const localizacao = this.localizacaoService.obterLocalizacaoSelecionada();

    if (!localizacao || !localizacao.tipoSimulacaoSelecionado) {
      this.edicaoBloqueada = false;
      return;
    }

    const tipoSimulacao = Number(localizacao.tipoSimulacaoSelecionado);

    // Bloqueia edição para planos (Nacional, Estadual, Municipal)
    // Permite edição apenas para Redes (Estadual ou Municipal)
    const tiposBloqueados = [
      TipoSimulacao.planejamentoNacional,
      TipoSimulacao.planejamento,
    ];

    this.edicaoBloqueada = tiposBloqueados.includes(tipoSimulacao);
  }

  async carregarMemoriaDeCalculo() {
    try {
      this.memoriaDeCalculo =
        await this.memoriaDeCalculoService.buscarMemoriaDeCalculoLocalizacao();
    } catch (error) {
      console.error('Erro ao buscar memória de cálculo', error);
    }
  }

  carregarPqrs(): void {
    this.pqrsDisponiveis =
      this.condicaoQualidadeStateService.getPQRNovasSalasPrediosPadrao();
  }

  carregarOverrides(): void {
    const valoresSalvos =
      this.condicaoQualidadeStateService.getPqrQuantidadeNovasConstrucoesValores();
    // Clona para evitar mutação direta antes de salvar
    this.overrides = valoresSalvos.map((o) => ({ ...o }));
  }

  // Busca ou cria um override para uma combinação específica
  getOverride(
    pqr: PQRNovasSalasPrediosDto,
    areaLocalidade: string,
    ehEducacaoInfantil: boolean,
  ): PqrQuantidadeNovasConstrucoesDto | undefined {
    const capacidade = this.extrairCapacidade(pqr);
    return this.overrides.find(
      (o) =>
        o.tamanhoPorNumeroSala === capacidade &&
        o.areaLocalidade === areaLocalidade &&
        o.pqrEducacaoInfantil === ehEducacaoInfantil,
    );
  }

  getQuantidade(
    pqr: PQRNovasSalasPrediosDto,
    areaLocalidade: string,
    ehEducacaoInfantil: boolean,
  ): number {
    // Primeiro verifica se há override (edição manual do usuário)
    const override = this.getOverride(pqr, areaLocalidade, ehEducacaoInfantil);
    if (override) {
      return override.quantidade;
    }

    // Se não há override, busca a quantidade calculada automaticamente da memória de cálculo
    if (
      this.memoriaDeCalculo?.resultadoParte9_9CustoTotalNovosPrediosSalas
        ?.detalhesConstrucoes
    ) {
      const etapa = ehEducacaoInfantil ? 'Educação Infantil' : 'EM/EF/EJA';
      const detalhe =
        this.memoriaDeCalculo.resultadoParte9_9CustoTotalNovosPrediosSalas.detalhesConstrucoes.find(
          (d) =>
            d.tipoConstrucao === pqr.descricaoTipoConstrucao &&
            d.areaLocalidade === areaLocalidade &&
            d.etapaModalidade === (ehEducacaoInfantil ? 'EI' : 'EF/EM/EJA'),
        );
      return detalhe ? detalhe.numero : 0;
    }

    return 0;
  }

  atualizarOverride(
    pqr: PQRNovasSalasPrediosDto,
    areaLocalidade: string,
    ehEducacaoInfantil: boolean,
    event: any,
  ): void {
    const valorInput = event.target.value.replace(/\D/g, '');
    const novaQuantidade = valorInput ? parseInt(valorInput, 10) : 0;
    const capacidade = this.extrairCapacidade(pqr);

    let override = this.getOverride(pqr, areaLocalidade, ehEducacaoInfantil);

    if (override) {
      // Atualiza existente
      override.quantidade = novaQuantidade;
      if (novaQuantidade === 0) {
        // Remove se for zero para limpar
        this.overrides = this.overrides.filter((o) => o !== override);
      }
    } else if (novaQuantidade > 0) {
      // Cria novo apenas se quantidade > 0
      override = {
        pqrEducacaoInfantil: ehEducacaoInfantil,
        tamanhoPorNumeroSala: capacidade,
        areaLocalidade: areaLocalidade,
        quantidade: novaQuantidade,
      };
      this.overrides.push(override);
    }

    this.atualizarDados();
  }

  calcularValorConstrucao(pqr: PQRNovasSalasPrediosDto): number {
    // Valor total da construção = área (m²) × preço por m²
    return pqr.dimensao * pqr.precoUnidadeMedida;
  }

  calcularDespesa(
    pqr: PQRNovasSalasPrediosDto,
    areaLocalidade: string,
    ehEducacaoInfantil: boolean,
  ): number {
    const quantidade = this.getQuantidade(
      pqr,
      areaLocalidade,
      ehEducacaoInfantil,
    );
    const valorConstrucao = this.calcularValorConstrucao(pqr);
    // Despesa de capital = quantidade × valor total da construção
    return quantidade * valorConstrucao;
  }

  calcularTotalDespesa(): number {
    let total = 0;

    // Soma todas as despesas de Educação Infantil
    this.getPqrsEducacaoInfantil().forEach((pqr) => {
      total += this.calcularDespesa(pqr, 'Urbana', true);
      total += this.calcularDespesa(pqr, 'Rural', true);
    });

    // Soma todas as despesas de Ensino Fundamental/Médio/EJA
    this.getPqrsEnsinoFundamentalMedioEja().forEach((pqr) => {
      total += this.calcularDespesa(pqr, 'Urbana', false);
      total += this.calcularDespesa(pqr, 'Rural', false);
    });

    return total;
  }

  atualizarDados(): void {
    this.condicaoQualidadeStateService.setPqrQuantidadeNovasConstrucoesValores(
      this.overrides,
    );
  }

  extrairCapacidade(pqr: PQRNovasSalasPrediosDto): number {
    switch (pqr.codPqrNovasSalasPredios) {
      case 1:
        return 1;
      case 2:
        return 2;
      case 3:
        return 4;
      case 4:
        return 6;
      case 5:
        return 12;
      case 6:
        return 16;
      case 7:
        return 24;
      case 8:
        return 32;
      default:
        return 0;
    }
  }

  // Métodos auxiliares para agrupar PQRs por etapa
  getPqrsEducacaoInfantil(): PQRNovasSalasPrediosDto[] {
    return this.pqrsDisponiveis.filter((pqr) => {
      // Assumindo que os primeiros PQRs são para EI, ou podemos usar alguma lógica específica
      // Por enquanto, vamos usar todos os PQRs para ambas as etapas (como no Vix)
      return true; // Todos os PQRs aparecem em ambas as etapas
    });
  }

  getPqrsEnsinoFundamentalMedioEja(): PQRNovasSalasPrediosDto[] {
    return this.pqrsDisponiveis.filter((pqr) => {
      // Mesma lógica - todos os PQRs aparecem em ambas as etapas
      return true;
    });
  }

  getTotalLinhasPorEtapa(etapa: 'EI' | 'EF'): number {
    const pqrs =
      etapa === 'EI'
        ? this.getPqrsEducacaoInfantil()
        : this.getPqrsEnsinoFundamentalMedioEja();
    return pqrs.length * 2; // Cada PQR tem 2 linhas (Urbana + Rural)
  }

  // Tooltip e Menu Actions
  abrirTooltips(): void {
    this.tooltipVisivel = true;
    setTimeout(() => {
      if (this.infoTooltip) this.infoTooltip.show();
      if (this.menuTooltip) this.menuTooltip.show();
    }, 100);
  }

  fecharTooltips(): void {
    this.tooltipVisivel = false;
    setTimeout(() => {
      if (this.infoTooltip) this.infoTooltip.hide();
      if (this.menuTooltip) this.menuTooltip.hide();
    }, 100);
  }

  chamarAcao(acao: string): void {
    if (acao === 'restaurarValoresComBaseNoPqr') {
      this.restaurarValoresComBaseNoPqr();
    }
  }

  restaurarValoresComBaseNoPqr(): void {
    const dialogRef = this.dialogRestaurarValores.open(
      DialogRestaurarValoresComponent,
      {
        panelClass: 'dialog-restaurar-valores',
        position: { top: '2%' },
      },
    );

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.overrides = [];
        this.atualizarDados();
        this.toastr.success('Valores restaurados (limpos).', 'Sucesso');
      }
    });
  }

  restaurarValoresPadrao(): void {
    this.restaurarValoresComBaseNoPqr();
  }

  validar(): ValidacaoResult {
    // Não há validação complexa aqui, pois valores vazios são aceitáveis (zero)
    return { valido: true, mensagens: [] };
  }

  async downloadCsvNovasSalasEdicao(): Promise<void> {
    try {
      this.isDownloadCsvNovasSalasEdicaoCarregando = true
      const pqrNovasConstrucoesQuantidadeList =
        this.condicaoQualidadeStateService.getPqrQuantidadeNovasConstrucoesValores();
      const pqrNovasSalasPredioList =
        this.pqrsDisponiveis.length > 0
          ? this.pqrsDisponiveis
          : this.condicaoQualidadeStateService.getPQRNovasSalasPrediosPadrao();

      const request: any = {
        diagnosticoDeMatriculaPorEscola:
          this.acessoOfertaEducacionalStateService.getProjecoesOfertaMatriculas() ||
          [],
        pqrNumeroDiasLetivosList:
          this.condicaoQualidadeStateService.getPQRNumeroDiasLetivosValores(),
        pqrCargaHorariaEnsinosList:
          this.condicaoQualidadeStateService.getPQRCargaHorarioEnsinoValores(),
        pqrTamanhoTurmaList:
          this.condicaoQualidadeStateService.getPQRTamanhoTurmasValores(),
        ...(this.featureFlagService.funcionalidadeHabilitada('1104-creche-idade') && this.featureFlagService.funcionalidadeHabilitada('1108-pqr-minimo-turma')
          ? { pqrTamanhoMinimoTurmaList: this.condicaoQualidadeStateService.getPQRMinimoTurmasValores() }
          : {}),
        pqrFormacaoJornadaRemuneracaoProfessoresList:
          this.condicaoQualidadeStateService.getPQRFormacaoJornadaRemuneracaoProfessoresValores(),
        pqrQuadroFuncionariosRemuneracaoList:
          this.condicaoQualidadeStateService.getPQRQuadroFuncionariosRemuneracaoValores(),
        pqrManutencaoFormacaoMaterialList:
          this.condicaoQualidadeStateService.getPQRManutencaoFormacaoMaterialValores(),
        pqrAlimentacaoEscolarList:
          this.condicaoQualidadeStateService.getPQRAlimentacaoEscolarValores(),
        pqrEncargosSociaisList:
          this.precoEncargosAdicionaisStateService.getPQREncargosSociaisValores() ||
          [],
        pqrAdicionaisList:
          this.precoEncargosAdicionaisStateService.getPQRAdicionaisValores() ||
          [],
        pqrNovasSalasPredioList: pqrNovasSalasPredioList,
        overridesNovasConstrucoes: pqrNovasConstrucoesQuantidadeList,
        pqrFatorPonderacaoList: this.condicaoQualidadeStateService.getPQRFatorPonderacaoValores()
      };

      const blob =
        await this.novasSalasEdicaoCsvService.exportarComBody(request);
      const dataAtual = new Date().toISOString().split('T')[0];
      const nomeArquivo = `modo_edicao_novas_salas_${dataAtual}.csv`;

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = nomeArquivo;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.toastr.success('CSV gerado com sucesso!', 'Sucesso');
    } catch (error) {
      console.error('Erro ao exportar CSV de novas salas:', error);
      this.toastr.error('Não foi possível gerar o CSV.', 'Erro');
    }finally {
      this.isDownloadCsvNovasSalasEdicaoCarregando = false
    }
  }
}
